import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Grid,
  Chip,
  Divider,
  Button,
  Container,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Visibility,
  ShoppingCartOutlined,
  InfoOutlined,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import configLocal from "../../config/config.local.json";
import QuickViewModal from "./QuickViewModal"; // Ensure this file exists in the same folder
import { FavoriteBorder, Favorite } from "@mui/icons-material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useCart } from "../../context/CartContext";
//TODO: wishlist icon toggle + backend integration, pagination controls, better error handling (toasts), loading states, mobile responsiveness, filter/sort options, and more robust token handling (refresh tokens, etc.)
//TODO: For local testing, you can hardcode the shop parameter in the API calls to match your backend setup (e.g., "test.spadeplatform.com") or adjust as needed.
//TODO: For the QuickViewModal, you can create a separate component that receives the selected product as a prop and displays its details in a modal. This keeps the code organized and allows for better separation of concerns.
//TODO: Consider implementing a debounce mechanism for the search functionality to reduce the number of API calls while the user is typing. This can be achieved using a custom hook or a utility function that delays the execution of the search until the user has stopped typing for a certain period (e.g., 300ms).
//TODO: For pagination, you can add "Previous" and "Next" buttons below the product grid that update the `page` state accordingly. You can also display the current page number and total pages if that information is available from the API response.
//TODO: Implement loading states (e.g., spinners or skeletons) while fetching products to improve user experience.
//TODO: Add error handling with toasts for better user feedback when API calls fail (e.g., failed to fetch products, failed to add to cart, etc.).
//TODO: Ensure mobile responsiveness by using MUI's Grid breakpoints and testing the layout on different screen sizes. You may need to adjust the number of columns or the size of the product cards for smaller screens.
//TODO: For token handling, consider implementing a refresh token mechanism to automatically refresh the access token when it expires, providing a smoother user experience without requiring them to log in again frequently.
//TODO: Add filter and sort options (e.g., by price, popularity, etc.) to allow users to easily find products that meet their criteria. This can be implemented with additional API parameters and UI controls (e.g., dropdowns or buttons).
//TODO: For the wishlist functionality, ensure that the API endpoints for adding/removing items from the wishlist are properly implemented in the backend and that the frontend updates the UI accordingly based on the user's actions and login status.
//TODO: Consider adding a "View Cart" button that opens the cart modal, allowing users to easily review their cart contents and proceed to checkout.
//TODO: For better code organization, you can create separate components for the product card, wishlist icon, and pagination controls, which can help keep the main dashboard component cleaner and more maintainable.
//TODO: Implement error handling for API calls, such as displaying error messages when fetching products fails or when adding to cart fails. This can be done using try-catch blocks and displaying toasts with appropriate messages.
//TODO: Add loading states (e.g., spinners or skeletons) while fetching products to improve user experience.
const ShopDashboard = () => {
  const BASE_URL = configLocal.baseURL;
  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);
  const searchQuery = query.get("search") || "";

  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [hoveredId, setHoveredId] = useState(null);
  const [wishlist, setWishlist] = useState([]); // store product IDs in wishlist

  // Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const { addToCart } = useCart();

  const limit = 12;

  const brand = {
    navy: "#2E4053",
    silverBlue: "#D1D8E0",
    textLight: "#34495E",
    muted: "#A6AAAD",
    white: "#FFFFFF",
    danger: "#E74C3C",
  };
  // may be null if not logged in

  const fetchProducts = async () => {
    try {
      const shopParam = "test"; // Adjust as needed for local testing

      const res = await axios.get(
        `${BASE_URL}/api/public/shop/products?shop=${shopParam}&search=${encodeURIComponent(
          searchQuery
        )}&page=${page}&limit=${limit}`
      );
      setProducts(res.data.data || []);
    } catch (err) {
      console.error("Failed to fetch products:", err);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [searchQuery, page]);

  // const fetchWishlist = async () => {
  //   try {
  //     const shopParam = "test.spadeplatform.com"; // adjust for your setup
  //     const token = localStorage.getItem("token");
  //     const headers = token ? { Authorization: `Bearer ${token}` } : {};
  //     console.log("Fetching wishlist with token:", token);
  //     console.log("Fetching wishlist with headers:", headers);
  //     const res = await axios.get(
  //       `${BASE_URL}/api/public/shop/wishlist?shop=${shopParam}`,
  //       { headers }
  //     );

  //     const ids = res.data.data
  //       ? res.data.data.map((item) => item.product_id)
  //       : [];
  //     setWishlist(ids);
  //   } catch (err) {
  //     if (err.response?.status === 401) {
  //       // user not logged in → ignore wishlist
  //       setWishlist([]);
  //     } else {
  //       console.error("Failed to fetch wishlist:", err);
  //     }
  //   }
  // };

  // useEffect(() => {
  //   fetchWishlist();
  // }, []);

  const handleOpenQuickView = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  // ❌ Delete all of this:
  // const [cartItems, setCartItems] = useState([]);
  // const [openCart, setOpenCart] = useState(false);
  // the entire handleAddToCart function
  // the getSessionId function

  // ✅ Replace with just this:

  const handleAddToCart = async (product) => {
    addToCart(product); // context handles state + backend
    toast.success("Added to cart!");
  };

  return (
    <Box sx={{ backgroundColor: "#F4F7F9", minHeight: "200vh" }}>
      <Container maxWidth="xxl" sx={{ py: 6 }}>
        {/* Header Section */}
        <Box
          sx={{
            mb: 6,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ borderLeft: `6px solid ${brand.navy}`, pl: 3 }}>
            <Typography
              variant="overline"
              sx={{
                fontWeight: 800,
                color: brand.muted,
                letterSpacing: 2,
                fontFamily: "Poppins",
              }}
            >
              Spade Platform
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 900, color: brand.navy, fontFamily: "Poppins" }}
            >
              {searchQuery ? `Search: ${searchQuery}` : "Products"}
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={4}>
          {products.map((product) => {
            const isOutOfStock = product.quantity_in_stock <= 0;
            const isLowStock =
              product.quantity_in_stock > 0 && product.quantity_in_stock <= 5;

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                <Card
                  onMouseEnter={() => setHoveredId(product.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  elevation={hoveredId === product.id ? 8 : 0}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "16px",
                    border: `1px solid ${brand.silverBlue}`,
                    transition:
                      "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                    position: "relative",
                    overflow: "hidden",
                    opacity: isOutOfStock ? 0.8 : 1,
                  }}
                >
                  {/* Image & Quick View Overlay */}
                  <Box
                    sx={{
                      position: "relative",
                      pt: "100%",
                      bgcolor: "#fff",
                      overflow: "hidden",
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={product.photo || "https://via.placeholder.com/300"}
                      sx={{
                        position: "absolute",
                        top: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        p: 1,
                        filter: isOutOfStock ? "grayscale(100%)" : "none",
                        transition: "transform 0.5s ease",
                        transform:
                          hoveredId === product.id ? "scale(1.08)" : "scale(1)",
                      }}
                    />

                    {/* Hover Overlay Buttons */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        bgcolor: "rgba(46, 64, 83, 0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                        opacity:
                          hoveredId === product.id && !isOutOfStock ? 1 : 0,
                        transition: "opacity 0.3s ease",
                        zIndex: 2,
                      }}
                    >
                      <Button
                        variant="contained"
                        startIcon={<Visibility />}
                        onClick={() => handleOpenQuickView(product)}
                        sx={{
                          bgcolor: brand.white,
                          color: brand.navy,
                          fontWeight: "bold",
                          textTransform: "none",
                          "&:hover": { bgcolor: brand.silverBlue },
                        }}
                      >
                        Quick View
                      </Button>
                    </Box>

                    {/* Badge Logic */}
                    <Chip
                      label={
                        isOutOfStock
                          ? "OUT OF STOCK"
                          : isLowStock
                          ? "LOW STOCK"
                          : "IN STOCK"
                      }
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 16,
                        left: 16,
                        fontWeight: 900,
                        fontSize: "0.65rem",
                        bgcolor: isOutOfStock
                          ? brand.danger
                          : isLowStock
                          ? "#F39C12"
                          : "#27AE60",
                        color: brand.white,
                        zIndex: 3,
                      }}
                    />
                  </Box>

                  <CardContent
                    sx={{
                      flexGrow: 1,
                      p: 3,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* SKU Tag - Fixed height container */}
                    {/* SKU Tag with Wishlist Icon */}
                    <Box
                      sx={{
                        height: 28,
                        mb: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between", // push SKU left, icon right
                      }}
                    >
                      {/* SKU */}
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: "'Roboto Mono', monospace",
                          color: brand.muted,
                          bgcolor: "#F0F2F4",
                          px: 1,
                          py: 0.5,
                          borderRadius: "4px",
                          fontWeight: 700,
                          display: "inline-block",
                        }}
                      >
                        SKU:{" "}
                        {product.sku ||
                          product.id.toString().substring(0, 8).toUpperCase()}
                      </Typography>

                      {/* Wishlist Icon */}
                      <Tooltip
                        title={
                          wishlist.includes(product.id)
                            ? "Remove from Wishlist"
                            : "Add to Wishlist"
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={async () => {
                            try {
                              const shopParam = "test.spadeplatform.com";
                              const headers = token
                                ? { Authorization: `Bearer ${token}` }
                                : {};

                              if (!token) {
                                toast.error(
                                  "You need to login to access your wishlist."
                                );
                                setTimeout(
                                  () => navigate("/shop/customerlogin"),
                                  1000
                                );
                                return;
                              }

                              const res = await axios.post(
                                `${BASE_URL}/api/public/wishlist/add?shop=${shopParam}`,
                                { product_id: product.id },
                                { headers }
                              );

                              if (wishlist.includes(product.id)) {
                                setWishlist((prev) =>
                                  prev.filter((id) => id !== product.id)
                                );
                              } else {
                                setWishlist((prev) => [...prev, product.id]);
                              }
                            } catch (err) {
                              console.error("Failed to update wishlist:", err);
                              toast.error(
                                "Failed to update wishlist. Try again."
                              );
                            }
                          }}
                        ></IconButton>
                      </Tooltip>
                    </Box>

                    {/* Product Name - Fixed height 2 lines with ellipsis */}
                    <Typography
                      variant="h6"
                      onClick={() => {
                        if (!isOutOfStock)
                          navigate(`/shop/ProductPage/${product.id}`);
                      }}
                      sx={{
                        fontWeight: 800,
                        color: brand.navy,
                        mb: 1,
                        fontSize: "1.15rem",
                        cursor: isOutOfStock ? "default" : "pointer",
                        height: "2.8rem", // Fixed height for 2 lines
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        lineHeight: "1.4rem",
                        "&:hover": {
                          color: isOutOfStock ? brand.navy : "#4A6177",
                        },
                      }}
                    >
                      {product.name}
                    </Typography>

                    {/* Stock Warning - Fixed height to prevent layout shift */}
                    <Box sx={{ height: 20, mb: 1 }}>
                      {isLowStock && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: brand.danger,
                            fontWeight: 800,
                            display: "block",
                          }}
                        >
                          ⚠️ Only {product.quantity_in_stock} units left!
                        </Typography>
                      )}
                    </Box>

                    {/* Description - Fixed height 2 lines with ellipsis */}
                    <Typography
                      variant="body2"
                      sx={{
                        color: brand.textLight,
                        height: "2.5rem", // Fixed height for 2 lines
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        textOverflow: "ellipsis",
                        fontSize: "0.85rem",
                        lineHeight: "1.25rem",
                        mb: 2,
                      }}
                    >
                      {product.description ||
                        "Premium quality enterprise inventory item."}
                    </Typography>

                    <Divider sx={{ mt: "auto", mb: 2 }} />

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: brand.muted,
                            fontWeight: 700,
                            display: "block",
                            mb: -0.5,
                          }}
                        >
                          UNIT PRICE
                        </Typography>
                        <Typography
                          variant="h5"
                          sx={{ fontWeight: 900, color: brand.navy }}
                        >
                          {product.selling_price}
                          <Box
                            component="span"
                            sx={{
                              fontSize: "0.9rem",
                              ml: 0.5,
                              color: brand.textLight,
                            }}
                          >
                            {product.currency || "EGP"}
                          </Box>
                        </Typography>
                      </Box>

                      <Tooltip title="View Full Details">
                        <IconButton
                          onClick={() =>
                            navigate(`/shopProductPage/${product.id}`)
                          }
                        >
                          <InfoOutlined sx={{ color: brand.navy }} />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Button
                      fullWidth
                      disabled={isOutOfStock}
                      variant="contained"
                      startIcon={<ShoppingCartOutlined />}
                      onClick={() => handleAddToCart(product)}
                      sx={{
                        bgcolor: brand.navy,
                        textTransform: "none",
                        fontWeight: "bold",
                        borderRadius: "8px",
                        py: 1,
                        "&:hover": { bgcolor: "#1C2A3D" },
                        "&.Mui-disabled": {
                          bgcolor: brand.silverBlue,
                          color: brand.muted,
                        },
                      }}
                    >
                      Add to Cart
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* External Modal Component */}
        <QuickViewModal
          open={isModalOpen}
          handleClose={() => setIsModalOpen(false)}
          product={selectedProduct}
          brand={brand}
        />
      </Container>
    </Box>
  );
};

export default ShopDashboard;
